import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  CreateReportInput,
  Report,
  ReportRunResult,
  ReportScheduleHealth,
  UpdateReportInput,
} from '@catalyst-psa/shared';

import { api, type DownloadResult } from '../../api/client.js';

const reportKeys = {
  all: ['reports'] as const,
  list: () => ['reports', 'list'] as const,
  detail: (id: string) => ['reports', 'detail', id] as const,
};

export function useReports(): UseQueryResult<Report[]> {
  return useQuery({
    queryKey: reportKeys.list(),
    queryFn: () => api.get<Report[]>('/reports'),
  });
}

export function useReport(id: string): UseQueryResult<Report> {
  return useQuery({
    queryKey: reportKeys.detail(id),
    queryFn: () => api.get<Report>(`/reports/${id}`),
    enabled: Boolean(id),
  });
}

export function useCreateReport(): UseMutationResult<Report, unknown, CreateReportInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateReportInput) => api.post<Report>('/reports', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: reportKeys.all });
    },
  });
}

export interface UpdateReportArgs {
  id: string;
  input: UpdateReportInput;
}

export function useUpdateReport(): UseMutationResult<Report, unknown, UpdateReportArgs> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: UpdateReportArgs) => api.patch<Report>(`/reports/${id}`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: reportKeys.all });
    },
  });
}

export function useDeleteReport(): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/reports/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: reportKeys.all });
    },
  });
}

/** Executes a report on demand and returns its rows (no email, no state change). */
export function useRunReport(): UseMutationResult<ReportRunResult, unknown, string> {
  return useMutation({
    mutationFn: (id: string) => api.post<ReportRunResult>(`/reports/${id}/run`),
  });
}

/** Sends a report's contents to its configured recipients now. */
export function useSendReport(): UseMutationResult<unknown, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post(`/reports/${id}/send`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: reportKeys.all });
    },
  });
}

/** Liveness of the scheduled-delivery pipeline (detects a stalled run-due timer). */
export function useScheduleHealth(enabled = true): UseQueryResult<ReportScheduleHealth> {
  return useQuery({
    queryKey: ['reports', 'schedule-health'] as const,
    queryFn: () => api.get<ReportScheduleHealth>('/reports/schedule-health'),
    enabled,
    refetchInterval: 60_000,
  });
}

/** Runs every due scheduled report now (manual kick of the run-due pipeline). */
export function useRunDueReports(): UseMutationResult<Report[], unknown, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<Report[]>('/reports/run-due'),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: reportKeys.all });
    },
  });
}

/** Triggers a browser download of a binary report result (CSV/PDF). */
function saveBlob(result: DownloadResult, fallbackName: string): void {
  const url = URL.createObjectURL(result.blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = result.fileName ?? fallbackName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export async function downloadReportCsv(id: string): Promise<void> {
  saveBlob(await api.download(`/reports/${id}/run.csv`), 'report.csv');
}

export async function downloadReportPdf(id: string): Promise<void> {
  saveBlob(await api.download(`/reports/${id}/run.pdf`), 'report.pdf');
}
